import { type FormEvent, useState } from "react";
import { BrandImage } from "./components/Brand";
import { Banner, Field } from "./components/ui";
import { ApiError } from "./lib/api";
import { requestCode, verifyCode } from "./lib/auth";

/** Sign in with a 6-digit code typed into the app (works inside an iPhone home-screen app). */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState(() => localStorageGet("lastEmail") ?? "");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestCode(email);
      localStorageSet("lastEmail", email.trim().toLowerCase());
      setStep("code");
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts. Wait a minute and try again."
          : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyCode(email, code);
      onSignedIn();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts. Wait a few minutes and try again."
          : "That code didn't work. Check it, or send a new one.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-narrow" style={{ paddingTop: "8vh" }}>
      <div className="signin-brand">
        <BrandImage height={38} />
        <span className="app-name">Hub</span>
      </div>
      <div className="card stack">
        <h1>Sign in</h1>
        {step === "email" ? (
          <form className="stack" onSubmit={sendCode}>
            <Field label="Email address">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            {error && <Banner kind="danger">{error}</Banner>}
            <button className="btn primary block" disabled={busy || !email}>
              {busy ? "Sending…" : "Email me a code"}
            </button>
            <p className="small muted">
              We'll email you a 6-digit code. Type it in here — don't tap any link.
            </p>
          </form>
        ) : (
          <form className="stack" onSubmit={submitCode}>
            <p>
              If <strong>{email}</strong> has an account, a 6-digit code is on its way.
            </p>
            <Field label="Code">
              <input
                className="code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
            </Field>
            {error && <Banner kind="danger">{error}</Banner>}
            <button className="btn primary block" disabled={busy || code.length !== 6}>
              {busy ? "Checking…" : "Sign in"}
            </button>
            <div className="row between">
              <button type="button" className="btn ghost sm" onClick={() => setStep("email")}>
                Use a different email
              </button>
              <button type="button" className="btn ghost sm" onClick={() => sendCode()} disabled={busy}>
                Send a new code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function localStorageGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

function localStorageSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {}
}
